import {
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  FinishDetailPairEntry,
  GroupFinishState,
  PendingReason
} from '../../api/useGroupFinish';
import { formatUsd, pnlColor } from '../../utils/format';

const SummaryCard = ({
  title,
  value,
  color
}: {
  title: string;
  value: string;
  color: string;
}) => (
  <Paper variant="outlined" sx={{ p: 1.5, minWidth: 110, flex: 1 }}>
    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color }}>
      {value}
    </Typography>
  </Paper>
);

const PendingTable = ({
  title,
  rows
}: {
  title: string;
  rows: FinishDetailPairEntry[];
}) => (
  <Stack spacing={0.5}>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'warning.main' }}>
      {title} ({rows.length})
    </Typography>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Market</TableCell>
            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>ruleId</TableCell>
            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Winning</TableCell>
            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Poly open</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.pairId}>
              <TableCell sx={{ fontSize: '0.7rem', maxWidth: 200 }}>
                <Typography noWrap sx={{ fontSize: '0.7rem' }}>
                  {row.pairName}
                </Typography>
              </TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>{row.ruleId ?? '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>{row.winningContracts}</TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>
                {formatUsd(row.polyRedeemUsd)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Stack>
);

export const FinishReportPanel = ({
  snapshot
}: {
  snapshot: GroupFinishState;
}) => {
  const { pnlSummary, computed, pending, excluded } = snapshot;

  const pendingByReason: Record<PendingReason, FinishDetailPairEntry[]> = {
    pending_kalshi: pending.filter(p => p.state === 'pending_kalshi'),
    pending_redeem: pending.filter(p => p.state === 'pending_redeem')
  };

  const groupNet = pnlSummary?.groupNetUsd ?? null;
  const kalshiNet = pnlSummary?.kalshiNetUsd ?? null;
  const polyNet = pnlSummary?.polymarketNetUsd ?? null;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <SummaryCard
          title="Group Net"
          value={formatUsd(groupNet)}
          color={pnlColor(groupNet)}
        />
        <SummaryCard
          title="Kalshi Net"
          value={formatUsd(kalshiNet)}
          color={pnlColor(kalshiNet)}
        />
        <SummaryCard
          title="Poly Net"
          value={formatUsd(polyNet)}
          color={pnlColor(polyNet)}
        />
      </Stack>

      {pnlSummary && (
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {pnlSummary.computedCount} de{' '}
          {pnlSummary.computedCount + pnlSummary.pendingCount} pares completos ·{' '}
          {pnlSummary.fullyFinished ? 'fully finished' : 'partially finished'}
        </Typography>
      )}

      <Stack spacing={0.5}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
          Completos ({computed.length})
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Market</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Res</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>ruleId</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Net</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  Kalshi / Poly
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {computed.map((market, idx) => (
                <TableRow key={market.pairId}>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{idx + 1}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', maxWidth: 180 }}>
                    <Typography noWrap sx={{ fontSize: '0.75rem' }}>
                      {market.pairName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {market.resolution === 'unresolved' ? (
                      <Chip label="UNRESOLVED" color="error" size="small" />
                    ) : (
                      <Chip
                        label={market.resolution.toUpperCase()}
                        color={market.resolution === 'yes' ? 'success' : 'default'}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {market.ruleId ?? '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.75rem',
                      color: pnlColor(market.pairNetUsd),
                      fontWeight: 500
                    }}
                  >
                    {formatUsd(market.pairNetUsd)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {formatUsd(market.kalshiLeg?.netUsd ?? null)} /{' '}
                    {formatUsd(market.polyLeg?.netUsd ?? null)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      {pendingByReason.pending_kalshi.length > 0 && (
        <PendingTable
          title="Pendientes — esperando settlement de Kalshi"
          rows={pendingByReason.pending_kalshi}
        />
      )}
      {pendingByReason.pending_redeem.length > 0 && (
        <PendingTable
          title="Pendientes — esperando redención en Polymarket"
          rows={pendingByReason.pending_redeem}
        />
      )}

      {excluded.length > 0 && (
        <Stack spacing={0.5}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
            Excluidos (resuelven más adelante) ({excluded.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Market</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>ruleId</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {excluded.map(row => (
                  <TableRow key={row.pairId}>
                    <TableCell sx={{ fontSize: '0.7rem', maxWidth: 220 }}>
                      <Typography noWrap sx={{ fontSize: '0.7rem' }}>
                        {row.pairName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>{row.ruleId ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Stack>
  );
};
